import {Link, useParams} from 'react-router-dom';
import {Navbar} from '@common/ui/navigation/navbar/navbar';
import {Footer} from '@common/ui/footer/footer';
import {Button} from '@common/ui/buttons/button';
import {Trans} from '@common/i18n/trans';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';
import {useSettings} from '@common/core/settings/use-settings';
import {LaunchOfferBanner} from '@app/landing/launch-offer-banner';
import {PageSeo} from '@app/landing/page-seo';
import {
  getCommonMistakes,
  getExamples,
} from '@app/landing/generic-page-sections';
import {
  getAlternativeTool,
  getRelatedTools,
} from './alternative-page-data';
import '../landing-page.css';

const MIGRATION_STEPS = (toolName: string) => [
  {
    title: `Sign up and create your LinkInBio page`,
    body: `Create a free LinkInBio account and set up your bio page with a drag-and-drop editor. Most people finish the basic layout in under 5 minutes.`,
  },
  {
    title: `Recreate your links`,
    body: `Copy the links from your ${toolName} page across, adding each one as a block, then set a custom alias, password, expiration, UTM tags, or retargeting pixel on the ones that need it.`,
  },
  {
    title: `Point your domain (optional)`,
    body: `If you have a custom domain, connect it in your LinkInBio settings so your bio page runs on your own URL instead of a shared one.`,
  },
  {
    title: `Update your bio link everywhere`,
    body: `Swap the link in your Instagram, TikTok, YouTube, and other bios to your new LinkInBio page. Keep your ${toolName} page live for a few days so you can compare click data before fully switching over.`,
  },
];

export function AlternativePage() {
  const {slug} = useParams();
  const {base_url, branding} = useSettings();
  const tool = getAlternativeTool(slug);

  if (!tool) {
    return <NotFoundPage />;
  }

  const siteName = branding?.site_name || 'LinkInBio';
  const related = getRelatedTools(tool);
  const [wf1, wf2] = tool.whyLinkInBio;
  const examples = getExamples(wf1?.title || 'links', wf2?.title || 'analytics');
  const mistakes = getCommonMistakes(tool.toolName + ' switchers');
  const pageUrl = `${(base_url || '').replace(/\/$/, '')}/alternatives/${tool.slug}`;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageSeo
        title={tool.metaTitle}
        description={tool.metaDescription}
        url={pageUrl}
      />
      <Navbar
        color="bg"
        border="border-b"
        className="lp-sticky-nav"
        menuPosition="homepage-navbar"
      />

      <main className="flex-auto">
        {/* HERO */}
        <header className="pt-80 md:pt-100 pb-40">
          <div className="landing-container max-w-[1000px]">
            <LaunchOfferBanner className="mb-24 inline-block" />
            <span className="section-label">
              {tool.toolName.toUpperCase()} ALTERNATIVE
            </span>
            <h1 className="alt-heading mb-24">{tool.h1}</h1>

            {tool.introParagraphs.map((p, i) => (
              <p key={i} className="text-base text-muted mb-16 max-w-[800px]">
                {p}
              </p>
            ))}

            <div className="flex flex-wrap gap-16 mt-32">
              <Button
                elementType={Link}
                to="/register"
                variant="flat"
                color="primary"
                size="md"
                radius="rounded-full"
              >
                <Trans message="Get started free" />
              </Button>
              <Button
                elementType={Link}
                to="/pricing"
                variant="outline"
                color="primary"
                size="md"
                radius="rounded-full"
              >
                <Trans message="See pricing" />
              </Button>
            </div>

            {/* QUICK VERDICT */}
            <div className="mt-40 grid sm:grid-cols-2 gap-16 max-w-[900px]">
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-20">
                <div className="text-xs font-bold uppercase tracking-wide text-primary mb-8">
                  Best if you...
                </div>
                <p className="text-sm">{tool.quickVerdict.bestForUs}</p>
              </div>
              <div className="rounded-xl border p-20">
                <div className="text-xs font-bold uppercase tracking-wide text-muted mb-8">
                  Stick with {tool.toolName} if you...
                </div>
                <p className="text-sm text-muted">
                  {tool.quickVerdict.bestForThem}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* PAIN POINTS */}
        <section className="pb-80 md:pb-100 bg-alt">
          <div className="landing-container max-w-[1000px]">
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              Why people look for a {tool.toolName} alternative
            </h2>
            <div className="grid md:grid-cols-2 gap-24">
              {tool.painPoints.map((point, i) => (
                <div key={i} className="rounded-xl border bg-white p-20">
                  <h3 className="font-semibold mb-8">{point.title}</h3>
                  <p className="text-sm text-muted">{point.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* COMPARISON TABLE */}
        <section className="pb-80 md:pb-100">
          <div className="landing-container max-w-[1000px]">
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              {tool.toolName} vs LinkInBio: feature-by-feature
            </h2>
            <div className="overflow-x-auto border rounded-xl">
              <table className="w-full text-sm border-collapse min-w-[600px]">
                <thead>
                  <tr className="bg-alt text-left">
                    <th className="p-16 font-semibold">Feature</th>
                    <th className="p-16 font-semibold text-primary">
                      LinkInBio
                    </th>
                    <th className="p-16 font-semibold text-muted">
                      {tool.toolName}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tool.comparisonRows.map((row, i) => (
                    <tr key={i} className="border-t align-top">
                      <td className="p-16 font-medium">{row.feature}</td>
                      <td className="p-16">{row.us}</td>
                      <td className="p-16 text-muted">{row.them}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* WHY LINKINBIO */}
        <section className="pb-80 md:pb-100 bg-alt">
          <div className="landing-container max-w-[1000px]">
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              Why creators choose LinkInBio
            </h2>
            <div className="grid md:grid-cols-3 gap-24">
              {tool.whyLinkInBio.map((item, i) => (
                <div key={i} className="rounded-xl border bg-white p-20">
                  <h3 className="font-semibold mb-8">{item.title}</h3>
                  <p className="text-sm text-muted">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="pb-80 md:pb-100">
          <div className="landing-container max-w-[900px]">
            <span className="section-label">EXAMPLES</span>
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              What a great LinkInBio page looks like
            </h2>
            <div className="grid md:grid-cols-2 gap-24">
              <div className="rounded-xl border bg-white p-24">
                <div className="text-xs font-bold uppercase tracking-wide text-muted mb-12">
                  Just starting out
                </div>
                <ul className="text-sm text-muted flex flex-col gap-8">
                  {examples.starting.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-primary/30 bg-white p-24">
                <div className="text-xs font-bold uppercase tracking-wide text-primary mb-12">
                  Established creators
                </div>
                <ul className="text-sm flex flex-col gap-8">
                  {examples.established.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* MIGRATION GUIDE */}
        <section className="pb-80 md:pb-100 bg-alt">
          <div className="landing-container max-w-[1000px]">
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              How to switch from {tool.toolName} to LinkInBio
            </h2>
            <div className="grid md:grid-cols-2 gap-24">
              {MIGRATION_STEPS(tool.toolName).map((step, i) => (
                <div key={i} className="flex gap-16">
                  <div className="flex-shrink-0 w-32 h-32 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-4">{step.title}</h3>
                    <p className="text-sm text-muted">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* COMMON MISTAKES */}
        <section className="pb-80 md:pb-100">
          <div className="landing-container max-w-[1000px]">
            <span className="section-label">AVOID THESE MISTAKES</span>
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              Common mistakes to avoid
            </h2>
            <div className="grid md:grid-cols-2 gap-24">
              {mistakes.map((mistake, i) => (
                <div key={i} className="rounded-xl border bg-white p-20">
                  <h3 className="font-semibold mb-8">{mistake.title}</h3>
                  <p className="text-sm text-muted">{mistake.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHO IT'S FOR */}
        <section className="pb-80 md:pb-100 bg-alt">
          <div className="landing-container max-w-[800px]">
            <span className="section-label">WHO IT'S FOR</span>
            <h2 className="alt-heading mb-24 text-2xl md:text-3xl">
              Is {siteName} right for you?
            </h2>
            <p className="text-base text-muted">
              {siteName} is built for anyone who wants a branded bio page
              with real link controls, not just a static list of URLs. If
              you're comparing more than one option,{' '}
              <Link to="/alternatives" className="text-primary font-medium">
                browse other alternatives
              </Link>{' '}
              to find the closest match for how you work.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="pb-80 md:pb-100">
          <div className="landing-container max-w-[800px]">
            <h2 className="alt-heading mb-32 text-2xl md:text-3xl">
              Frequently asked questions
            </h2>
            <div className="flex flex-col gap-20">
              {tool.faq.map((item, i) => (
                <div key={i} className="rounded-xl border bg-white p-20">
                  <h3 className="font-semibold mb-8">{item.q}</h3>
                  <p className="text-sm text-muted">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pb-80 md:pb-100 text-center bg-alt">
          <div className="landing-container max-w-[700px]">
            <h2 className="alt-heading mb-16 text-2xl md:text-3xl">
              {tool.ctaLine}
            </h2>
            <LaunchOfferBanner className="mb-16 inline-block" />
            <Button
              elementType={Link}
              to="/register"
              variant="flat"
              color="primary"
              size="md"
              radius="rounded-full"
              className="mt-16"
            >
              <Trans message="Get started free" />
            </Button>
          </div>
        </section>

        {/* RELATED ALTERNATIVES — internal linking */}
        {related.length > 0 && (
          <section className="pb-80 md:pb-100">
            <div className="landing-container max-w-[1000px]">
              <h2 className="alt-heading mb-24 text-xl">
                Compare more link-in-bio alternatives
              </h2>
              <div className="flex flex-wrap gap-12">
                {related.map(r => (
                  <Link
                    key={r.slug}
                    to={`/alternatives/${r.slug}`}
                    className="rounded-full border px-20 py-10 text-sm font-medium hover:bg-hover"
                  >
                    {r.toolName} alternative
                  </Link>
                ))}
                <Link
                  to="/alternatives"
                  className="rounded-full border px-20 py-10 text-sm font-medium hover:bg-hover"
                >
                  See all alternatives
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer className="mx-14 md:mx-40" />
    </div>
  );
}
